import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToggleButton, ToggleButtonGroup, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { UnorderedListOutlined, PlusOutlined, ToolOutlined } from '@ant-design/icons';
import { isDesktopShell } from 'utils/isDesktopShell';
import { getCurrentAppType } from 'utils/dynamicConfig';

export default function NavToggle() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const shell = isDesktopShell();
  const appType = getCurrentAppType();
  const isOpsDomain = appType.includes('ops');

  // Detect small screens (smaller than MUI's 'sm' breakpoint)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine the currently selected route
  // Takes last part of URL excluding query string
  const selectedRoute = location.pathname.split('/').at(-1)?.split('?').at(0);

  // Handle toggle button changes
  const handleToggle = (_event: React.MouseEvent<HTMLElement>, newRoute: string | null) => {
    if (newRoute && newRoute !== selectedRoute) {
      navigate(newRoute);
    }
  };

  // Don't show any toggle buttons for ops domains
  if (isOpsDomain) {
    return null;
  }

  // Get tooltip text based on current route and shell status
  const getCreateOrderTooltip = () => {
    if (shell && selectedRoute !== 'order-create') {
      return 'Create Order (Ctrl+N)';
    }
    return 'Create Order';
  };

  const getOrdersListTooltip = () => {
    if (shell && selectedRoute !== 'orders') {
      return 'Orders List (Ctrl+L)';
    }
    return 'Orders List';
  };

  // Default order management navigation
  return (
    <ToggleButtonGroup
      value={selectedRoute}
      exclusive
      onChange={handleToggle}
      color="secondary"
      size="small"
      aria-label="Order actions"
      sx={{ ml: 2 }}
    >
      <Tooltip title={getCreateOrderTooltip()}>
        <ToggleButton value="order-create" aria-label="Create Order" sx={{ whiteSpace: 'nowrap' }}>
          <PlusOutlined style={{ marginRight: 8 }} />
          {isMobile ? 'Create' : 'Create Order'}
        </ToggleButton>
      </Tooltip>      
      <Tooltip title={getOrdersListTooltip()}>
        <ToggleButton value="orders" aria-label="Orders List" sx={{ whiteSpace: 'nowrap' }}>
          <UnorderedListOutlined style={{ marginRight: 8 }} />
          {isMobile ? 'List' : 'Orders List'}
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );
}
